import { useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import PanelSkeleton from '@shared/components/PanelSkeleton';
import RetryableError from '@shared/components/RetryableError';
import { ApiRequestError } from '@api/client/apiClient';
import { useUrlState } from '@shared/hooks/useUrlState';
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue';
import { useActivity } from './hooks/useActivity';
import ActivityFilters from './components/ActivityFilters';
import ActivityTable from './components/ActivityTable';
import ActivityExport from './components/ActivityExport';
import { ACTIVITY_PAGE_SIZE } from './constants';

export default function ActivityPage() {
  const [module, setModule] = useUrlState<string>('module', '');
  const [status, setStatus] = useUrlState<string>('status', '');
  const [search, setSearch] = useUrlState<string>('search', '');
  const [correlationId, setCorrelationId] = useUrlState<string>('correlationId', '');
  const [operationId, setOperationId] = useUrlState<string>('operationId', '');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(ACTIVITY_PAGE_SIZE);

  const debouncedSearch = useDebouncedValue(search, 300);
  const debouncedCorrelationId = useDebouncedValue(correlationId, 300);
  const debouncedOperationId = useDebouncedValue(operationId, 300);

  const { data, isLoading, error, refetch } = useActivity({
    module: module || undefined,
    status: status || undefined,
    search: debouncedSearch || undefined,
    correlationId: debouncedCorrelationId || undefined,
    operationId: debouncedOperationId || undefined,
    page,
    size,
  });

  function handleClearFilters() {
    setModule('');
    setStatus('');
    setSearch('');
    setCorrelationId('');
    setOperationId('');
    setPage(0);
  }

  if (error && !data) {
    const corrId = error instanceof ApiRequestError ? error.apiError.correlationId : undefined;
    return (
      <RetryableError
        message={error.message ?? 'Failed to load activity'}
        correlationId={corrId}
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <div data-testid="page-activity" className="flex flex-col h-full p-4 gap-3">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Activity Log</Typography>
        <ActivityExport />
      </Box>

      <ActivityFilters
        module={module}
        status={status}
        search={search}
        correlationId={correlationId}
        operationId={operationId}
        onModuleChange={(v) => {
          setModule(v);
          setPage(0);
        }}
        onStatusChange={(v) => {
          setStatus(v);
          setPage(0);
        }}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        onCorrelationIdChange={(v) => {
          setCorrelationId(v);
          setPage(0);
        }}
        onOperationIdChange={(v) => {
          setOperationId(v);
          setPage(0);
        }}
        onClear={handleClearFilters}
      />

      {isLoading && !data ? (
        <PanelSkeleton />
      ) : (
        <div className="flex-1 overflow-y-auto">
          <ActivityTable
            entries={data?.items ?? []}
            loading={isLoading}
            page={page}
            rowsPerPage={size}
            totalRows={data?.totalItems ?? 0}
            onPageChange={setPage}
            onRowsPerPageChange={(newSize) => {
              setSize(newSize);
              setPage(0);
            }}
          />
        </div>
      )}
    </div>
  );
}
